import { useTranslation, Trans } from 'react-i18next';
import React, { HTMLProps } from 'react';
import cx from 'clsx';

import styles from './ReviewAndPayment.module.scss';
import { HookedTipSelector } from './TipSelector';
import { InvoiceSummary as InvoiceSummaryComponent } from './InvoiceSummary/InvoiceSummary';
import { Subheading } from './Subheading/Subheading';
import { useWindowSize } from 'hooks/breakpoints';
import { CloverPaymentSection } from './CloverPaymentSection/CloverPaymentSection';
import { useMerchant } from 'hooks/context/merchant';
import { useCheckoutValidity } from '../context/CheckoutValidity';
import { TabletPlaceOrderButton } from '../PlaceOrderButton/PlaceOrderButton';
import { Icon } from 'components/Icon';
import { useSubmitOrder } from '../context/SubmitOrder/SubmitOrder';
import { isPaymentProcessorIpg } from '../../../util/IpgPay';
import { CHECKOUT_TEST_ID } from '../const';
import { isTaxSupported } from 'locales/helpers';
import { PerksSection } from 'components/PerksSection';
import { InvoiceLineItems } from 'components/LineItems/InvoiceLineItems';
import { CartBanners } from 'components/CartBanners';

export type PaymentInfoErrors = {
  errors: Record<Clover.InputFieldType, string | undefined>;
  isAllTouched: boolean;
};

type ReviewAndPaymentProps = HTMLProps<HTMLDivElement>;

export const ReviewAndPayment: React.FC<ReviewAndPaymentProps> = ({ children, className, ...props }) => {
  const { t } = useTranslation();
  const { setPaymentFormValidity } = useCheckoutValidity();
  const { merchant } = useMerchant();
  const { isDesktop } = useWindowSize();
  const { error, hasUnrecoverableError } = useSubmitOrder();

  const isIpg = isPaymentProcessorIpg(merchant?.paymentMethods);

  return (
    <div className={cx(styles.wrapper, className)} {...props}>
      <header>
        <h2 className={styles.heading}>{t('checkoutPage.heading')}</h2>
        <Subheading />
        <CartBanners gutters="horizontal" />
      </header>

      <InvoiceLineItems />

      <PerksSection />

      <HookedTipSelector />

      <InvoiceSummaryComponent showTax={isTaxSupported(merchant?.locale)} tipsEnabled={!!merchant?.tipsEnabled} />

      {!isIpg && !hasUnrecoverableError && <CloverPaymentSection onValidityChange={setPaymentFormValidity} />}

      <footer>
        {error && (
          <p className={styles.errorLine}>
            <Icon name="error" />
            <span>{t(error)}</span>
          </p>
        )}
        {isDesktop && <TabletPlaceOrderButton type="submit" className={styles.submit} />}
        <div className={styles.termsAndConditions}>
          <Trans i18nKey="checkoutPage.termsAndConditions">
            By placing an order, you agree to
            <a href="/consumer/terms" target="_blank" rel="noopener noreferrer">
              Clover's Terms of Service
            </a>
            and
            <a href={t('footerLinks.privacy')} target="_blank" rel="noopener noreferrer">
              Privacy Policy
            </a>
          </Trans>
          {!!merchant?.privacyPolicyPath && (
            <a
              className={styles.privacyPolicy}
              href={merchant.privacyPolicyPath}
              target="_blank"
              rel="noopener noreferrer"
              data-testid={CHECKOUT_TEST_ID.merchant_privacy_policy}
            >
              {' '}
              {t('checkoutPage.merchantPrivacyPolicy', { merchantName: merchant?.name })}
            </a>
          )}
        </div>
      </footer>
    </div>
  );
};
